import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { PEOPLE_KEY, type FunctionSummary } from '../people/types';
import { Notice } from '../ui/Notice';
import { RULES_KEY } from './types';

/**
 * Owner 2026-10-30 21:59 („jak w zakresie wybieram realizatora i technika to dodaj po jednej pozycji w kosztorysie od
 * razu”), review I2: „Zakres → funkcja” — for each scope item that wants a person, the function (its rule „Miejsce
 * w obsadzie”, which also gives the quote line „Realizator FOH”…). The owner's setting: nothing happens until saved.
 */
type Row = { scope: string; label: string; ruleId: string | null; functionId: string | null; own: { ruleId: string; name: string } | null };
type Setting = { items: Row[]; version: string };
const KEY = [...RULES_KEY, 'scope-functions'] as const;

/** The first proposal by name: „Realizator…” for the realizacja items (exactly „Realizator” first), „Technik…” for Technika. */
export function bestFunction(scope: string, functions: readonly FunctionSummary[]): string {
  const want = scope === 'technika' ? 'technik' : 'realizator';
  const norm = (f: FunctionSummary) => (f.name ?? '').trim().toLocaleLowerCase('pl-PL');
  return (functions.find((f) => norm(f) === want) ?? functions.find((f) => norm(f).startsWith(want)))?.id ?? '';
}

export function ScopeFunctions() {
  const queryClient = useQueryClient();
  const saved = useQuery({ queryKey: KEY, queryFn: () => api<Setting>('/api/rules/scope-functions') });
  const functions = useQuery({ queryKey: [...PEOPLE_KEY, 'functions'], queryFn: () => api<{ functions: FunctionSummary[] }>('/api/people/functions') });
  const [draft, setDraft] = useState<Record<string, string> | null>(null);
  const [proposal, setProposal] = useState(false);
  const [done, setDone] = useState(false);
  /** M-f: saved meanwhile by someone else — the rows as they are now are shown. */
  const [stale, setStale] = useState(false);
  const fromSaved = (setting: Setting) => setDraft(Object.fromEntries(setting.items.map((r) => [r.scope, r.functionId ?? ''])));
  // once both are in: what is saved, or (nothing saved at all) the proposal by name, not saved yet
  useEffect(() => {
    if (draft || !saved.data || !functions.data) return;
    const none = saved.data.items.every((r) => r.functionId === null);
    setDraft(Object.fromEntries(saved.data.items.map((r) => [r.scope, none ? bestFunction(r.scope, functions.data.functions) : (r.functionId ?? '')])));
    setProposal(none && saved.data.items.some((r) => bestFunction(r.scope, functions.data.functions) !== ''));
  }, [draft, saved.data, functions.data]);
  const save = useMutation({
    // M-e: a row with only the owner's own rule is not the setting's: not sent (3rd review 2: with the setting's too, it is)
    mutationFn: () =>
      api<Setting>('/api/rules/scope-functions', { method: 'PUT', body: { version: saved.data!.version, items: saved.data!.items.filter((r) => !r.own || r.ruleId).map((r) => ({ scope: r.scope, functionId: draft?.[r.scope] || null })) } }),
    onSuccess: async (after) => {
      queryClient.setQueryData(KEY, after);
      setProposal(false);
      setStale(false);
      setDone(true);
      await queryClient.invalidateQueries({ queryKey: RULES_KEY });
    },
    onError: (e) => {
      const current = e instanceof ApiError && e.status === 409 ? (e.current as Setting | undefined) : undefined;
      if (!current) return;
      queryClient.setQueryData(KEY, current);
      fromSaved(current);
      setProposal(false);
      setStale(true);
    },
  });
  const error = save.error instanceof ApiError && save.error.status !== 409 ? save.error : null;
  return (
    <section className="panel scope-functions" aria-label="Zakres → funkcja" data-feature-id="rules.scope_functions">
      <h2>Zakres → funkcja</h2>
      <p className="muted">Każdy zaznaczony zakres daje w kosztorysie jedną pozycję tej funkcji (np. Realizacja FOH → „Realizator FOH”) i miejsce w obsadzie. Istniejące kosztorysy się nie zmieniają — pozycje pojawiają się przy zmianie zakresu wydarzenia albo przy nowym kosztorysie.</p>
      {(saved.isLoading || functions.isLoading) && <p className="muted" role="status">Wczytywanie…</p>}
      {(saved.error ?? functions.error) && <p className="alert" role="alert">Nie udało się wczytać ustawienia albo listy funkcji.</p>}
      {proposal && <p className="notice">Propozycja — niezapisana. Sprawdź funkcje i kliknij „Zapisz”.</p>}
      {draft && saved.data && functions.data && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setDone(false);
            save.mutate();
          }}
        >
          {saved.data.items.map((r) =>
            r.own && !r.ruleId ? (
              <div key={r.scope} className="field">
                <span>{r.label}</span>
                <span className="muted">własna reguła „{r.own.name}” (zmienisz ją w „Reguły”)</span>
              </div>
            ) : (
            <label key={r.scope} className="field">
              <span>{r.label}</span>
              <select aria-label={r.label} value={draft[r.scope] ?? ''} onChange={(e) => setDraft({ ...draft, [r.scope]: e.target.value })}>
                <option value="">— brak —</option>
                {/* 3rd review 2: next to an own rule the setting's may only stay or go */}
                {functions.data.functions.filter((f) => !r.own || f.id === r.functionId).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name ?? '(funkcja)'}
                  </option>
                ))}
              </select>
              {r.own && <span className="muted">także własna reguła „{r.own.name}” — ustaw tu „— brak —” albo usuń własną w „Reguły”</span>}
            </label>
            ),
          )}
          {stale && (
            <p className="alert" role="alert">
              Ktoś zmienił to ustawienie w międzyczasie — poniżej jest aktualne. Sprawdź i zapisz jeszcze raz.
            </p>
          )}
          {error && (
            <p className="alert" role="alert">
              {error.message}
            </p>
          )}
          <div className="actions">
            <button className="btn primary" type="submit" disabled={save.isPending}>
              {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
            </button>
          </div>
        </form>
      )}
      {done && <Notice onClose={() => setDone(false)}>Zapisano. Pozycje pojawią się przy zmianie zakresu wydarzenia albo przy nowym kosztorysie.</Notice>}
    </section>
  );
}
